import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { loadLegalDoc, type LegalSlug } from '../../content/legal'

interface LegalSectionProps {
  slug: LegalSlug
}

export function LegalSection({ slug }: LegalSectionProps) {
  const { i18n } = useTranslation()
  const [content, setContent] = useState<string>('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    loadLegalDoc(slug, i18n.language).then((md) => {
      if (cancelled) return
      setContent(md)
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [slug, i18n.language])

  if (loading) {
    return <p className="text-gray-500 text-sm">…</p>
  }
  if (!content) {
    return <p className="text-gray-500 text-sm">Document unavailable.</p>
  }

  return (
    <div className="text-gray-700 leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ ...props }) => (
            <h1 className="text-2xl font-bold text-gray-900 mb-4 mt-6 first:mt-0" {...props} />
          ),
          h2: ({ ...props }) => (
            <h2 className="text-lg font-bold text-gray-900 mb-3 mt-5" {...props} />
          ),
          h3: ({ ...props }) => (
            <h3 className="text-base font-semibold text-gray-900 mb-2 mt-4" {...props} />
          ),
          p: ({ ...props }) => <p className="text-gray-700 leading-relaxed mb-3" {...props} />,
          ul: ({ ...props }) => (
            <ul className="list-disc pl-5 space-y-1 mb-3 text-gray-700" {...props} />
          ),
          ol: ({ ...props }) => (
            <ol className="list-decimal pl-5 space-y-1 mb-3 text-gray-700" {...props} />
          ),
          li: ({ ...props }) => <li className="text-gray-700" {...props} />,
          strong: ({ ...props }) => (
            <strong className="font-semibold text-gray-900" {...props} />
          ),
          em: ({ ...props }) => <em className="italic" {...props} />,
          a: ({ ...props }) => (
            <a
              className="text-primary hover:underline"
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            />
          ),
          hr: ({ ...props }) => <hr className="my-6 border-gray-200" {...props} />,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}

export default LegalSection
